// A deck drawn on SEVERAL PowerPoint templates (AB#445, plans/BID_OFFICE_TEMPLATES.md "Defaults and
// references", "Rendering and export with several templates"). A bid keeps a library of PowerPoint
// templates; a deck has a DEFAULT template and master (the artefact's own choice, else the bid
// default on its own master); each slide may name its own template (`slide.template`, absent = the
// deck default), and its `layout` is a layout id of THAT template — unique inside one package, so
// the pair also names the layout's master. Each slide is painted from its own template and the
// master of its layout, and its theme tokens (`accent1`, …) resolve there: a slide moved to another
// template recolours, as in PowerPoint.
//
// Pure helpers over the deck's directory (`GET /v1/slides/templates?room=`, lib/api.ts#DeckTemplates):
// which (template, master) a slide is drawn on — the SAME rule as the backend's `slideTemplateRef`
// (backend/domain/office-templates.ts) — which pairs a deck uses (the views the editor always loads),
// and the layout menus' groups (template › master › layout), listed from the directory whether their
// views are in or not (AB#480). Deliberately outside the vendored
// renderer's sources (render.ts, theme.ts, render-entry.ts): the renderer is handed ONE RenderTheme
// per slide and never learns there are several.
import type { DeckTemplate, DeckTemplateRef, DeckTemplates } from '../lib/api'
import { layoutChoices, type LayoutChoice } from './deck'
import { DEFAULT_THEME, themeFromTemplate, type RenderTheme, type SlidesTemplateLike } from './theme'

/** What a slide says about where it is drawn (a SlideSnapshot fits). */
export interface SlideTemplateKeys {
  template?: string
  layout?: string
}

/** The slice of a library template the resolution reads. */
export interface TemplateMastersLike {
  id: string
  master: number
  masters: ReadonlyArray<{ layouts: ReadonlyArray<{ id: string }> }>
}

/**
 * The (template, master) a slide is drawn on, or null when no PowerPoint template resolves (the
 * default theme and the built-in layouts). Its own template while it names one of the library, else
 * the deck default (a template since deleted falls back there too); the master holding its layout id
 * in that template, else (a slide made before layout ids stores a layout TYPE, resolved by type by
 * theme.ts#layoutFor) the template's HOME master: the deck's master for the deck default template,
 * the template's own master for another. Mirrors backend/domain/office-templates.ts#slideTemplateRef.
 */
export function slideTemplateRef(
  slide: SlideTemplateKeys,
  deck: DeckTemplateRef | null,
  templates: readonly TemplateMastersLike[],
): DeckTemplateRef | null {
  if (!deck) return null
  const t = templates.find((x) => x.id === slide.template) ?? templates.find((x) => x.id === deck.templateId)
  if (!t) return null
  const home = t.id === deck.templateId ? deck.master : t.master
  if (slide.layout) {
    const at = t.masters.findIndex((m) => m.layouts.some((l) => l.id === slide.layout))
    if (at >= 0) return { templateId: t.id, master: at }
  }
  return { templateId: t.id, master: home }
}

/** A pair as a map key; '' = no template. */
export const refKey = (ref: DeckTemplateRef | null): string => (ref ? `${ref.templateId}\u0000${ref.master}` : '')

/** The library template a directory lists under `id`. */
export const templateOf = (dir: DeckTemplates | null, id: string): DeckTemplate | undefined => dir?.templates.find((t) => t.id === id)

/** The deck's default pair, while the directory lists its template (else no template resolves). */
export function deckRefOf(dir: DeckTemplates | null): DeckTemplateRef | null {
  return dir?.deck && templateOf(dir, dir.deck.templateId) ? dir.deck : null
}

/** The pair of a slide in this directory (null = none: the default theme). */
export const refOfSlide = (dir: DeckTemplates | null, slide: SlideTemplateKeys): DeckTemplateRef | null =>
  slideTemplateRef(slide, dir?.deck ?? null, dir?.templates ?? [])

/**
 * The pairs a deck is drawn on: the deck default first (where "New slide" lands), then every other
 * pair its slides use, once each. The editor loads these views only.
 */
export function usedTemplateRefs(dir: DeckTemplates | null, slides: Iterable<SlideTemplateKeys>): DeckTemplateRef[] {
  const out = new Map<string, DeckTemplateRef>()
  const deck = deckRefOf(dir)
  if (deck) out.set(refKey(deck), deck)
  for (const s of slides) {
    const ref = refOfSlide(dir, s)
    if (ref && !out.has(refKey(ref))) out.set(refKey(ref), ref)
  }
  return [...out.values()]
}

/** A template's masters, its home master first, then the others in the package's order. */
function mastersHomeFirst(t: DeckTemplate, home: number): number[] {
  const all = t.masters.map((_, i) => i)
  return all.includes(home) ? [home, ...all.filter((i) => i !== home)] : all
}

/**
 * Every pair the layout menus offer, in their order: the deck default template first (its master
 * first, then its other masters), then the library's other templates (oldest first), each on its
 * own master first. Empty when no template resolves.
 */
export function menuTemplateRefs(dir: DeckTemplates | null): DeckTemplateRef[] {
  const deck = deckRefOf(dir)
  if (!dir || !deck) return []
  const own = templateOf(dir, deck.templateId) as DeckTemplate
  const ordered = [own, ...dir.templates.filter((t) => t.id !== own.id)]
  return ordered.flatMap((t) =>
    mastersHomeFirst(t, t.id === deck.templateId ? deck.master : t.master).map((master) => ({ templateId: t.id, master })),
  )
}

/** The `template` a layout choice of this pair carries: none for the deck default template. */
export const choiceTemplate = (ref: DeckTemplateRef | null, dir: DeckTemplates | null): string | undefined =>
  ref && ref.templateId !== dir?.deck?.templateId ? ref.templateId : undefined

/**
 * A group's heading: "Capgemini 2025 › Standard", or the template's name alone when it has one
 * master. `masterN` names a master its author left unnamed (1-based).
 */
export function groupLabel(t: DeckTemplate, master: number, masterN: (n: number) => string): string {
  if (t.masters.length <= 1) return t.name
  return `${t.name} › ${t.masters[master]?.name?.trim() || masterN(master + 1)}`
}

/** One group of the layout menus: the layouts of one template on one master, drawn on that pair. */
export interface LayoutGroup {
  /** Stable across renders: the pair's refKey ('' = no template). */
  key: string
  ref: DeckTemplateRef | null
  /** Its heading; '' when the deck offers a single pair (the menus then look as they always did). */
  label: string
  /** What its layouts are drawn with: its own template's theme on its own master. */
  theme: RenderTheme
  layouts: LayoutChoice[]
  /**
   * Why its lines cannot be picked (AB#480): its view, which draws them and holds what a pick writes,
   * has not come yet, or failed. They are then the directory's names. Absent = they can.
   */
  unavailable?: 'loading' | 'failed'
  /** Another template than the deck default, which the menus collapse under one heading; absent = the deck default's. */
  template?: { id: string; name: string; masters: number }
}

/** The views drawn so far: a pair's view, null when it has none (failed, or gone), undefined while it loads. */
export type ViewOf = (ref: DeckTemplateRef) => SlidesTemplateLike | null | undefined

const themes = new WeakMap<SlidesTemplateLike, RenderTheme>()
/**
 * A view's render theme, ONE object per view (so the thumbnails' render contexts, and their
 * memoised drawings, survive every other change — AB#283); the default theme for none.
 */
export function themeOfView(view: SlidesTemplateLike | null | undefined): RenderTheme {
  if (!view) return DEFAULT_THEME
  let theme = themes.get(view)
  if (!theme) {
    theme = themeFromTemplate(view)
    themes.set(view, theme)
  }
  return theme
}

const choices = new WeakMap<SlidesTemplateLike, Map<string, LayoutChoice[]>>()
/** A view's layout choices, tagged with their pair; the same array for the same view and tag. */
function choicesOf(view: SlidesTemplateLike, template: string | undefined, master: number): LayoutChoice[] {
  let byTag = choices.get(view)
  if (!byTag) {
    byTag = new Map()
    choices.set(view, byTag)
  }
  const tag = `${template ?? ''}\u0000${master}`
  let list = byTag.get(tag)
  if (!list) {
    list = layoutChoices(view, { ...(template ? { template } : {}), master })
    byTag.set(tag, list)
  }
  return list
}

/** A master's layouts by name, as the directory lists them: enough to list and filter, not to pick (no placeholders). */
function directoryChoices(t: DeckTemplate, master: number, template: string | undefined): LayoutChoice[] {
  return (t.masters[master]?.layouts ?? []).map((l) => ({
    layout: l.id,
    type: l.type,
    name: l.name || l.type,
    elements: [],
    ...(template ? { template } : {}),
    master,
  }))
}

/**
 * The layout menus' groups (template › master › layout): EVERY pair of the directory, in
 * menuTemplateRefs' order, so a menu opens and filters without any view (AB#480). A pair whose view
 * is not in lists the directory's names, `unavailable`. No template resolving = one group of the
 * built-in layouts, as a template-less bid always had. The deck default's pair without a view (it
 * failed) offers the built-in layouts, as a failed template always did.
 */
export function layoutGroups(dir: DeckTemplates | null, viewOf: ViewOf, masterN: (n: number) => string): LayoutGroup[] {
  const pairs = menuTemplateRefs(dir)
  const deck = deckRefOf(dir)
  if (!dir || !deck || !pairs.length) return [{ key: '', ref: null, label: '', theme: DEFAULT_THEME, layouts: layoutChoices(null) }]
  return pairs.map((ref) => {
    const view = viewOf(ref)
    const t = templateOf(dir, ref.templateId) as DeckTemplate
    const tag = choiceTemplate(ref, dir)
    const builtIn = view === null && refKey(ref) === refKey(deck)
    const unavailable = view === undefined ? 'loading' : !view && !builtIn ? 'failed' : null
    return {
      key: refKey(ref),
      ref,
      label: pairs.length > 1 ? groupLabel(t, ref.master, masterN) : '',
      theme: themeOfView(view),
      layouts: view ? choicesOf(view, tag, ref.master) : builtIn ? layoutChoices(null) : directoryChoices(t, ref.master, tag),
      ...(unavailable ? { unavailable } : {}),
      ...(t.id !== deck.templateId ? { template: { id: t.id, name: t.name, masters: t.masters.length } } : {}),
    }
  })
}
